import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, ChevronLeft, ChevronRight, Filter, Image as ImageIcon, Video } from 'lucide-react';
import { apiService } from '../services/apiService';

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaActiva, setCategoriaActiva] = useState('all');
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [itemActivo, setItemActivo] = useState(null);
  const [itemIndex, setItemIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [galeriaResponse, categoriasResponse] = await Promise.all([
          apiService.getGaleria(),
          apiService.getCategoriasGaleria()
        ]);

        if (galeriaResponse.estado === 1) {
          setItems(galeriaResponse.data);
        }

        if (categoriasResponse.estado === 1) {
          setCategorias(categoriasResponse.data);
        }
      } catch (error) {
        console.error('Error cargando galería:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filtrar items por categoría
  const itemsFiltrados = categoriaActiva === 'all'
    ? items
    : items.filter(item => item.categoria_nombre === categoriaActiva || item.id_categoria_galeria === parseInt(categoriaActiva));

  // Abrir lightbox
  const openLightbox = (item, index) => {
    setItemActivo(item);
    setItemIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Cerrar lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setItemActivo(null);
    document.body.style.overflow = 'unset';
  };

  // Navegar en lightbox
  const nextItem = () => {
    const newIndex = (itemIndex + 1) % itemsFiltrados.length;
    setItemIndex(newIndex);
    setItemActivo(itemsFiltrados[newIndex]);
  };

  const prevItem = () => {
    const newIndex = (itemIndex - 1 + itemsFiltrados.length) % itemsFiltrados.length;
    setItemIndex(newIndex);
    setItemActivo(itemsFiltrados[newIndex]);
  };

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  if (loading) {
    return (
      <section className="min-h-screen bg-black flex flex-col items-center justify-center pt-20">
        <motion.div 
          className="w-16 h-16 border-4 border-yellow-600/30 border-t-yellow-500 rounded-full" 
          animate={{ rotate: 360 }} 
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }} 
        />
        <p className="text-yellow-500/70 mt-4 text-sm tracking-widest uppercase">Cargando galería...</p>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-black text-white pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* --- ENCABEZADO --- */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-block text-yellow-500 text-sm font-semibold tracking-widest uppercase mb-3 border-b border-yellow-600/50 pb-1">
            Galería de Eventos
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Nuestros <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">Momentos</span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent mx-auto rounded-full" />
        </motion.div>

        {/* --- FILTROS POR CATEGORÍA --- */}
        {categorias.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            <button
              onClick={() => setCategoriaActiva('all')}
              className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 flex items-center gap-2 ${
                categoriaActiva === 'all'
                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black shadow-lg shadow-yellow-500/25'
                  : 'bg-neutral-900/50 border border-yellow-600/30 text-gray-300 hover:border-yellow-500/60 hover:text-yellow-500'
              }`}
            >
              <Filter className="w-4 h-4" />
              Todos
            </button>
            
            {categorias.map((cat, index) => (
              <button
                key={cat.id}
                onClick={() => setCategoriaActiva(cat.id.toString())}
                className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
                  categoriaActiva === cat.id.toString()
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black shadow-lg shadow-yellow-500/25'
                    : 'bg-neutral-900/50 border border-yellow-600/30 text-gray-300 hover:border-yellow-500/60 hover:text-yellow-500'
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {cat.nombre}
              </button>
            ))}
          </motion.div>
        )}

        {/* --- GRID DE GALERÍA --- */}
        {itemsFiltrados.length === 0 ? (
          <div className="text-center py-20 bg-neutral-900/30 rounded-2xl border border-yellow-600/20">
            <ImageIcon className="w-16 h-16 text-yellow-600/40 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No hay elementos en esta categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {itemsFiltrados.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className="group relative aspect-square overflow-hidden rounded-xl bg-neutral-900 border border-yellow-600/20 cursor-pointer"
                onClick={() => openLightbox(item, index)}
              >
                {/* Imagen o Video */}
                {item.es_video || item.url_completa?.match(/\.(mp4|webm|mov|avi)$/i) ? (
                  <video
                    src={item.url_completa}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={item.url_completa}
                    alt={item.titulo}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                  {/* Badge de categoría */}
                  {item.categoria_nombre && (
                    <div className="absolute top-3 left-3">
                      <span className="bg-black/70 backdrop-blur-sm text-yellow-500 text-xs font-bold px-3 py-1 rounded-full border border-yellow-600/30">
                        {item.categoria_nombre}
                      </span>
                    </div>
                  )}

                  {/* Icono de play si es video */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 bg-yellow-500/90 rounded-full flex items-center justify-center backdrop-blur-sm shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      {item.es_video || item.url_completa?.match(/\.(mp4|webm|mov|avi)$/i) ? (
                        <Play className="w-6 h-6 text-black ml-1" fill="black" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-black" />
                      )}
                    </div>
                  </div>

                  {/* Título */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-semibold text-sm line-clamp-2">{item.titulo}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* --- LIGHTBOX (MODAL PANTALLA COMPLETA) --- */}
        <AnimatePresence>
          {lightboxOpen && itemActivo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center"
              onClick={closeLightbox}
            >
              {/* Botón cerrar */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 w-12 h-12 bg-black/50 border border-yellow-600/50 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-600/20 transition-all duration-300 z-50"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Botón anterior */}
              {itemsFiltrados.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); prevItem(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 border border-yellow-600/50 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-600/20 transition-all duration-300"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {/* Contenido */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="max-w-6xl max-h-[90vh] w-full mx-4 flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
              >
                {itemActivo.es_video || itemActivo.url_completa?.match(/\.(mp4|webm|mov|avi)$/i) ? (
                  <video
                    src={itemActivo.url_completa}
                    className="max-w-full max-h-[80vh] rounded-lg"
                    controls
                    autoPlay
                  />
                ) : (
                  <img
                    src={itemActivo.url_completa}
                    alt={itemActivo.titulo}
                    className="max-w-full max-h-[80vh] rounded-lg object-contain"
                  />
                )}

                {/* Información */}
                {itemActivo.titulo && (
                  <div className="mt-4 text-center">
                    <p className="text-white font-semibold text-lg">{itemActivo.titulo}</p>
                    {itemActivo.categoria_nombre && (
                      <p className="text-yellow-500 text-sm mt-1">{itemActivo.categoria_nombre}</p>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Botón siguiente */}
              {itemsFiltrados.length > 1 && (
                <button
                  onClick={(e) => { e.stopPropagation(); nextItem(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 border border-yellow-600/50 rounded-full flex items-center justify-center text-yellow-500 hover:bg-yellow-600/20 transition-all duration-300"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}

              {/* Contador */}
              {itemsFiltrados.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-600/30">
                  <span className="text-yellow-500 font-medium text-sm">
                    {itemIndex + 1} / {itemsFiltrados.length}
                  </span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Gallery;